import {
  SfdtBlock,
  SfdtCharacterFormat,
  SfdtDocument,
  SfdtInline,
  SfdtParagraphFormat,
} from "./types";

// Minimal SFDT renderer. SFDT is Syncfusion's JSON document format used by
// Zegal's DTE. We only need to produce HTML that Word can ingest via
// body.insertHtml — round-trip fidelity is not a goal in phase 1.
//
// Handled: paragraphs, basic inline text formatting, alignment, bookmarks.
// Not handled: tables, images, lists, headers/footers, styles, fields.

export function sfdtToHtml(sfdt: SfdtDocument | null | undefined): string {
  if (!sfdt?.sections?.length) return "<p></p>";
  return sfdt.sections
    .map((section) => (section.blocks || []).map(renderBlock).join(""))
    .join("");
}

function renderBlock(block: SfdtBlock): string {
  const align = alignmentStyle(block.paragraphFormat);
  const inlines = block.inlines || [];
  const inner = renderInlines(inlines);
  const style = align ? ` style="${align}"` : "";
  return `<p${style}>${inner || "&nbsp;"}</p>`;
}

function renderInlines(inlines: SfdtInline[]): string {
  let html = "";
  for (const inline of inlines) {
    if (inline.bookmarkType === 0 && inline.name) {
      html += `<span data-bookmark-start="${escapeAttr(inline.name)}"></span>`;
    } else if (inline.bookmarkType === 1 && inline.name) {
      html += `<span data-bookmark-end="${escapeAttr(inline.name)}"></span>`;
    } else if (typeof inline.text === "string") {
      html += wrapWithFormatting(inline.characterFormat, escapeHtml(inline.text));
    }
  }
  return html;
}

function wrapWithFormatting(format: SfdtCharacterFormat | undefined, text: string): string {
  if (!format) return text;
  let result = text;
  if (format.bold) result = `<strong>${result}</strong>`;
  if (format.italic) result = `<em>${result}</em>`;
  if (format.underline && format.underline !== "None") result = `<u>${result}</u>`;
  const inlineStyles: string[] = [];
  if (format.fontSize) inlineStyles.push(`font-size: ${format.fontSize}pt`);
  if (format.fontFamily) inlineStyles.push(`font-family: ${cssSafe(format.fontFamily)}`);
  if (format.fontColor) inlineStyles.push(`color: ${cssSafe(format.fontColor)}`);
  if (inlineStyles.length) {
    result = `<span style="${inlineStyles.join("; ")}">${result}</span>`;
  }
  return result;
}

function alignmentStyle(format: SfdtParagraphFormat | undefined): string {
  switch (format?.textAlignment) {
    case "Center":
      return "text-align: center";
    case "Right":
      return "text-align: right";
    case "Justify":
      return "text-align: justify";
    default:
      return "";
  }
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function escapeAttr(s: string): string {
  return escapeHtml(s);
}

function cssSafe(s: string): string {
  // Strip anything that could break out of a CSS value.
  return s.replace(/[^a-zA-Z0-9 ,#()\-_.%]/g, "");
}
